// BoxSearch.tsx
import {
  Button,
  Flex,
  FormControl,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { debounce } from 'lodash'
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FiCamera } from 'react-icons/fi'
import QRCodeScanner from './QRCodeScanner'

interface BoxSearchProps {
  searchType: string;
  setSearchType: (value: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  /**
   * Dispara a busca. Recebe o termo porque `setSearchValue` só vale no próximo render:
   * quem chamasse `onSearch()` logo depois estaria pedindo a busca do termo ANTERIOR.
   */
  onSearch: (termo?: string) => void;
  autoOpenScanner?: boolean;
  onScannerOpen?: () => void;
  loading?: boolean;
}

const BoxSearch = React.forwardRef<HTMLInputElement, BoxSearchProps>(({
  searchType,
  setSearchType,
  searchValue,
  setSearchValue,
  onSearch,
  autoOpenScanner = false,
  onScannerOpen,
  loading = false,
}, ref) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [localSearch, setLocalSearch] = useState(searchValue);
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  /*
    O termo viaja junto com o pedido, em vez de sair do estado.

    Antes era `setSearchValue(x); onSearch()`. `setSearchValue` só vale no render seguinte,
    então `onSearch` lia o termo ANTERIOR e pedia a busca errada: procurar "luciane" e
    depois o CPF de uma delas devolvia as duas Lucianes de novo, e só o segundo clique em
    Buscar acertava — no segundo clique o estado já tinha alcançado o que fora digitado.
  */
  const pedirBusca = useRef(onSearch)
  const sincronizar = useRef(setSearchValue)
  useEffect(() => {
    pedirBusca.current = onSearch
    sincronizar.current = setSearchValue
  }, [onSearch, setSearchValue])

  // Criado uma vez. Recriar o debounce a cada render (era o que as deps do useCallback
  // faziam) deixa timers antigos vivos, e cada um dispara a busca do seu próprio termo.
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const cleanedValue = value.trim()
        if (cleanedValue.length < 3) return
        sincronizar.current(cleanedValue)
        pedirBusca.current(cleanedValue)
      }, 1000),
    [],
  )

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setLocalSearch(value)
    debouncedSearch(value)
  }

  const handleSearchClick = () => {
    const cleanedValue = localSearch.trim();

    if (!cleanedValue || cleanedValue.length < 3) {
      toast({
        title: "Digite pelo menos 3 caracteres para buscar.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // Cancela o debounce pendente: sem isto, o clique busca o termo certo e o timer
    // atrasado busca de novo logo depois, e a resposta que chegar por último manda.
    debouncedSearch.cancel()
    // O estado do pai também acompanha: a paginação e a limpeza da lista dependem dele.
    // Quem dispara a busca agora é a chamada com o termo; o estado é só o registro.
    setSearchValue(cleanedValue)
    onSearch(cleanedValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchClick()
    }
  }

  useEffect(() => {
    setLocalSearch(searchValue)
  }, [searchValue])

  return (
    <>
      <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} mb={4}>
        Buscar Credenciado:
      </Text>
      <Flex direction="row" gap={3} align="center">
        <FormControl flex={1}>
          <Input
            autoComplete="off"
            ref={inputRef}
            placeholder="Buscar por nome, CPF, e-mail ou QR Code..."
            variant="filled"
            size="sm"
            value={localSearch}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            h="70px"
            fontSize="lg"
          />
        </FormControl>

        <IconButton
          icon={<Icon as={FiCamera} boxSize="38px" />}
          aria-label="Abrir leitor de QRCode"
          size="sm"
          onClick={onOpen}
          h="70px"
          w="70px"
        />

        <Button
          colorScheme="blue"
          onClick={handleSearchClick}
          isDisabled={loading}
          size="sm"
          h="70px"
        >
          Buscar
        </Button>
      </Flex>
      {loading && (
        <Flex justify="center" align="center" mt={2}>
          <Spinner size="sm" />
        </Flex>
      )}

      <QRCodeScanner
        isOpen={isOpen}
        onClose={onClose}
        onScan={(data) => {
          // Mesmo motivo do botão: o código lido vai junto com o pedido. O QR era o pior
          // caso — chegava inteiro de uma vez e buscava o que estivesse na tela antes.
          setLocalSearch(data)
          debouncedSearch.cancel()
          setSearchValue(data)
          onSearch(data)
        }}
      />
    </>
  );
});

export default BoxSearch;
